<template>
  <b-navbar
    toggleable="md"
    sticky
    variant="iiasa"
    class="main-navbar navbar-dark">

    <b-navbar-brand :to="{name: 'index'}" class="brand one-line">
      <brand/>
    </b-navbar-brand>

    <b-navbar-nav class="ml-auto">
      <b-navbar-toggle target="nav_collapse" class="float-right"></b-navbar-toggle>
    </b-navbar-nav>

    <b-collapse is-nav id="nav_collapse">

      <!-- Right aligned nav items -->
      <b-navbar-nav class="ml-auto">
        <b-button
          pill
          v-if="$auth.isLoggedIn"
          size="sm" @click="onHelp"
          class="help-button border-0"
          title="Start help tour"
          variant="outline-light">
          <icon class="d-none d-md-block" icon="question-circle"/>
          <span class="d-md-none">Help</span>
        </b-button>

        <b-nav-item-dropdown
          v-if="$auth.isLoggedIn"
          text="Workspaces" class="v-step-workspace-actions" right>
          <b-dropdown-item v-access exact :to="{name: 'workspaces'}">Workspaces</b-dropdown-item>
          <b-dropdown-divider/>
          <b-dropdown-header>Recent</b-dropdown-header>
          <b-dropdown-item
            v-for="workspace in recentWorkspaces"
            :key="workspace.id"
            :to="{ name: 'workspace', params: { id: workspace.id }}">
            {{workspace.name}}
          </b-dropdown-item>
          <b-dropdown-item v-if="recentWorkspaces.length === 0" disabled>No recent workspaces</b-dropdown-item>
        </b-nav-item-dropdown>

        <b-nav-item v-if="$auth.isLoggedIn" exact :to="{name: 'downloads'}" right>
          Downloads
        </b-nav-item>
        <b-nav-item v-if="$auth.isLoggedIn" exact :to="{name: 'docs'}" right>
          Documentation
        </b-nav-item>
        <b-nav-item exact :to="{name: 'license'}" right>License</b-nav-item>
        <b-nav-item exact :to="{name: 'about'}" right>About</b-nav-item>

        <b-nav-item
          v-for="item in extraMainMenu" :key="item.name"
          exact :to="{name: item.name}"
          right class="text-nowrap">
          <span v-html="item.title"></span>
        </b-nav-item>

        <b-nav-item-dropdown
          v-for="[title, menu] in extraSubMenu"
          :key="title"
          right>
          <template v-slot:button-content>
            <span v-html="title"></span>
          </template>
          <b-dropdown-item v-for="item in menu" :key="item.name" :to="{name: item.name}">
            <span v-html="item.title"></span>
          </b-dropdown-item>
        </b-nav-item-dropdown>

        <b-nav-item-dropdown
          id="user-menu"
          v-if="$auth.isLoggedIn"
          right>
          <template slot="button-content">
            {{$auth.username || 'guest'}}
          </template>
          <b-dropdown-item v-access v-if="$auth.isLoggedIn" :to="{name: 'profile'}">Profile</b-dropdown-item>
          <b-dropdown-item v-if="canUploadFiles" :to="{name: 'uploads'}">Uploads</b-dropdown-item>
          <b-dropdown-item v-if="canGenerateSnapshots" :to="{name: 'snapshots'}">Snapshots</b-dropdown-item>
          <b-dropdown-item v-if="$auth.isAdmin" :to="{name: 'caches'}">Caches</b-dropdown-item>
          <b-dropdown-item v-if="$auth.isAdmin" :to="{name: 'runs'}">Scenario management</b-dropdown-item>
          <b-dropdown-item v-access :to="{name: 'feedback'}">Feedback</b-dropdown-item>
          <b-dropdown-item v-b-modal.modalLogout>Logout</b-dropdown-item>
        </b-nav-item-dropdown>

        <b-button
          pill
          size="sm" @click="toggleDarkTheme"
          class="border-0"
          :title="useDarkTheme ? 'Switch to bright layout' : 'Switch to dark layout'"
          variant="outline-light">
          <icon class="d-none d-md-block" :icon="useDarkTheme ? 'sun' : 'moon'"/>
          <span class="d-md-none">{{useDarkTheme ? 'Light mode' : 'Dark mode'}}</span>
        </b-button>

      </b-navbar-nav>

    </b-collapse>

    <b-modal id="modalLogout" title="Logout" @ok="$auth.logout($router)">
      <p class="my-4">You are going to logout. All unsaved changes to workspace settings and panels will be lost!</p>
    </b-modal>
  </b-navbar>

</template>

<script>
  import {mapActions, mapMutations, mapState} from 'vuex';
  import {NOTIFY, TOGGLE_DARK_THEME} from '../store/mutation-types';

  export default {
    computed: {
      extraMainMenu() {
        return this.extraMenu['main'] || [];
      },
      extraSubMenu() {
        return Object.entries(this.extraMenu).filter(([name, _]) => name !== 'main');
      },
      extraMenu() {
        const {$router, $auth} = this;
        return (this.$config.extraMenu || []).filter(m => {
          const target = $router.resolve({name: m.name});
          if (!target) return false;
          return target.route.meta.checkAccess($auth);
        }).reduce((elements, next) => {
          const menu = next.menu || 'main';
          return {...elements, [menu]: [...(elements[menu] || []), next]};
        }, {});
      },
      currentWorkspaceId() {
        return this.$route.params.id === undefined ? undefined : parseInt(this.$route.params.id, 10);
      },
      ...mapState({
        workspaces: state => state.workspaces,
        auth: state => state.auth,
        recentWorkspaces(state) {
          const {workspaceMap} = this;
          return state.ui.workspaceSelector.recent
            .map(id => workspaceMap.get(id))
            .filter(w => !!w);
        },
        useDarkTheme: state => state.ui.useDarkTheme,
      }),
      currentWorkspace() {
        const {currentWorkspaceId, workspaceMap} = this;
        return workspaceMap.get(currentWorkspaceId);
      },
      workspaceMap() {
        return new Map(this.workspaces.map(w => [w.id, w]));
      },
    },
    asyncComputed: {
      canUploadFiles: {
        get() {
          return this.$auth.canUploadFiles();
        },
        default: false,
      },
      canGenerateSnapshots: {
        get() {
          return this.$auth.canGenerateSnapshots();
        },
        default: false,
      },
    },
    methods: {
      onHelp() {
        this.toggleTour({skip: false, auth: this.$auth});
        this.$nextTick(() => {
          this.$tours.tour && this.$tours.tour.start();
        });
      },
      waitGUIUpdate(callback, timeout = 100) {
        // don't know yet why $nextTick doesn't work for that
        return new Promise(resolve => {
          setTimeout(() => {
            if (callback) {
              callback();
            }
            resolve();
          }, timeout);
        });
      },
      ...mapMutations({
        notify: NOTIFY,
        toggleDarkTheme: TOGGLE_DARK_THEME,
      }),
      ...mapActions(['toggleTour']),
    },
  };
</script>

<style scoped>
  .main-navbar .brand {
    max-width: 75%;
  }

  .main-navbar .navbar-brand {
    color: #ffffff !important;
  }

  .main-navbar .navbar-nav > .nav-item > a {
    color: #ffffff !important;
  }

  .main-navbar .navbar-nav > .nav-item:hover > a,
  .main-navbar .navbar-nav > .nav-item:focus > a {
    color: #e9ecef !important;
  }
</style>
